import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Snackbar, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import UndoIcon from '@mui/icons-material/UndoOutlined';
import RedoIcon from '@mui/icons-material/RedoOutlined';
import { useAuth } from '../../context/AuthContext';
import { fetchMulticamSet, fetchVideoById } from '../../graphqlService';
import {
  EditingApiError,
  fetchBrandKit,
  fetchEdit,
  fetchEditingAllowance,
  fetchAiStatus,
  saveEdit,
  speakVoiceover,
  type BrandKit,
  type EditRecord,
  type EditingAllowance,
} from '../../services/editingApi';
import { debugLog } from '../../utils/debugLog';
import type { EditList } from '../../utils/editing/editList';
import { outputDurationMs } from '../../utils/editing/editList';
import { cuesFromWords, parseCaptionFile, placeFileCues, type TimedWord } from '../../utils/editing/captions';
import PageContainer from '../ui/PageContainer';
import { useVideoTranscript } from '../Transcript/useVideoTranscript';
import { brand } from '../../theme';
import BrandKitDialog from './BrandKitDialog';
import CommandBar from './CommandBar';
import ExportsPanel from './ExportsPanel';
import PreviewOverlay, { type PreviewCue } from './PreviewOverlay';
import PreviewTransport from './PreviewTransport';
import { commit, historyOf, redo, replacePresent, undo, type History } from './editStudioModel';
import CaptionsPanel from './panels/CaptionsPanel';
import CutPanel from './panels/CutPanel';
import LooksPanel from './panels/LooksPanel';
import OutputPanel from './panels/OutputPanel';
import SoundPanel from './panels/SoundPanel';
import { usePreviewAudio } from './usePreviewAudio';
import { usePreviewPlayer } from './usePreviewPlayer';

type PanelKey = 'cut' | 'looks' | 'captions' | 'sound' | 'output' | 'export';

const AUTOSAVE_MS = 1500;
const STUDIO_FRAME_SX = { maxWidth: 1680, pt: { xs: 2, md: 2.5 }, pb: 6 } as const;

interface SourceMedia {
  videoUrl: string;
  name: string;
  /** The video whose transcript carries the captions. */
  transcriptVideoId: string | null;
  canTranscribe: boolean;
  multicam: boolean;
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  return Boolean(el.closest('input, textarea, select, [contenteditable="true"]'));
};

/** The quick-edit studio: preview an edit list on the existing player, change it, export it. */
const EditStudio: React.FC = () => {
  const { editId } = useParams<{ editId: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [edit, setEdit] = useState<EditRecord | null>(null);
  const [source, setSource] = useState<SourceMedia | null>(null);
  const [kit, setKit] = useState<BrandKit | null>(null);
  const [allowance, setAllowance] = useState<EditingAllowance | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [history, setHistory] = useState<History | null>(null);
  const [savedList, setSavedList] = useState<EditList | null>(null);
  const [saveState, setSaveState] = useState<'saved' | 'pending' | 'saving' | 'conflict' | 'error'>('saved');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [panel, setPanel] = useState<PanelKey>('cut');
  const [selection, setSelection] = useState<{ fromMs: number | null; toMs: number | null }>({ fromMs: null, toMs: null });
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [kitOpen, setKitOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [voices, setVoices] = useState<string[]>([]);
  const versionRef = useRef(0);
  const savingRef = useRef(false);

  const list = history?.present ?? null;

  // --- Loading ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!editId) return;
      try {
        const [record, brandKit, plan] = await Promise.all([fetchEdit(editId), fetchBrandKit(), fetchEditingAllowance().catch(() => null)]);
        if (cancelled) return;
        if (!record) {
          setLoadError('This edit no longer exists.');
          return;
        }
        setEdit(record);
        setTitle(record.title);
        versionRef.current = record.version;
        setHistory(historyOf(record.list));
        setSavedList(record.list);
        setKit(brandKit);
        setAllowance(plan);
        const token = (await getToken()) || '';
        if (record.sourceKind === 'video' && record.videoId) {
          const video = await fetchVideoById(record.videoId, token);
          if (cancelled) return;
          setSource({ videoUrl: video.url, name: video.name, transcriptVideoId: record.videoId, canTranscribe: Boolean(plan?.planId === 'promax'), multicam: false });
        } else if (record.multicamSetId) {
          const set = await fetchMulticamSet(record.multicamSetId);
          if (cancelled) return;
          const master = set?.angles.find((angle) => angle.angleKey === 'A') || set?.angles[0];
          const audioAngle = set?.audioSource?.videoId || set?.angles.find((angle) => angle.angleKey === set.audioAngleKey)?.videoId || master?.videoId || null;
          if (!set || !master) {
            setLoadError('This multicam set no longer exists.');
            return;
          }
          setSource({ videoUrl: master.videoUrl, name: set.name, transcriptVideoId: audioAngle, canTranscribe: Boolean(set.ownerFeatures?.transcripts), multicam: true });
        }
      } catch (error) {
        debugLog('Edit studio failed to load', error);
        if (!cancelled) setLoadError(error instanceof Error ? error.message : 'The edit couldn’t be loaded.');
      }
    })();
    return () => { cancelled = true; };
  }, [editId, getToken]);

  useEffect(() => {
    fetchAiStatus().then((status) => setVoices(status.available ? status.voices : [])).catch((error) => debugLog('AI status failed', error));
  }, []);

  const refreshAllowance = useCallback(() => {
    fetchEditingAllowance().then(setAllowance).catch((error) => debugLog('Allowance refresh failed', error));
  }, []);

  // --- Changing and saving ---------------------------------------------------
  const change = useCallback((next: EditList) => {
    setHistory((current) => (current ? commit(current, next) : current));
  }, []);

  const dirty = Boolean(list && savedList && list !== savedList && JSON.stringify(list) !== JSON.stringify(savedList));

  const save = useCallback(async (titleToSave?: string) => {
    if (!edit || !history || savingRef.current || saveState === 'conflict') return;
    const current = history.present;
    const nextTitle = titleToSave ?? null;
    if (JSON.stringify(current) === JSON.stringify(savedList) && nextTitle === null) return;
    savingRef.current = true;
    setSaveState('saving');
    try {
      const saved = await saveEdit(edit.id, versionRef.current, current, nextTitle);
      versionRef.current = saved.version;
      setEdit(saved);
      setSavedList(saved.list);
      // The server's canonical copy replaces ours only if nothing changed meanwhile.
      setHistory((h) => (h && h.present === current ? replacePresent(h, saved.list) : h));
      if (h2IsSame(current, saved.list)) setSavedList(saved.list);
      setSaveState('saved');
      setSaveMessage(null);
    } catch (error) {
      const apiError = error instanceof EditingApiError ? error : new EditingApiError(error);
      if (apiError.code === 'EDIT_CONFLICT') {
        setSaveState('conflict');
        setSaveMessage(apiError.message);
      } else {
        setSaveState('error');
        setSaveMessage(apiError.message);
      }
    } finally {
      savingRef.current = false;
    }
  }, [edit, history, savedList, saveState]);

  /** Generates a spoken line's voice: the server saves the new version, and the studio takes it as its own. */
  const speak = useCallback(async (voiceoverId: string) => {
    if (!edit) return;
    if (dirty || savingRef.current) await save();
    if (savingRef.current || saveState === 'conflict') throw new Error('Wait for the edit to finish saving, then try again.');
    const saved = await speakVoiceover(edit.id, versionRef.current, voiceoverId);
    versionRef.current = saved.version;
    setEdit(saved);
    setSavedList(saved.list);
    setHistory((h) => (h ? replacePresent(h, saved.list) : h));
    setToast('Voice generated');
  }, [dirty, edit, save, saveState]);

  // Autosave a moment after the last change.
  useEffect(() => {
    if (!dirty || saveState === 'conflict') return undefined;
    setSaveState((state) => (state === 'saving' ? state : 'pending'));
    const timer = window.setTimeout(() => { save(); }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [dirty, list, save, saveState]);

  // Undo and redo from the keyboard.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const meta = event.metaKey || event.ctrlKey;
      if (meta && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        setHistory((h) => (h ? (event.shiftKey ? redo(h) : undo(h)) : h));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // --- Preview ---------------------------------------------------------------
  const player = usePreviewPlayer(list ?? EMPTY_LIST);
  usePreviewAudio({ list: list ?? EMPTY_LIST, assets: kit?.assets || [], outputMs: player.outputMs, playing: player.playing, video: player.element, muted });

  const transcript = useVideoTranscript(list?.captions?.source === 'transcript' ? source?.transcriptVideoId : null, { withWords: true, canStart: Boolean(source?.canTranscribe) });
  const words = useMemo<TimedWord[]>(() => {
    const out: TimedWord[] = [];
    transcript.lines.forEach((line) => {
      if (line.words && line.words.length) line.words.forEach((word) => out.push({ text: word.text, start: word.start, end: word.end }));
      else out.push({ text: line.text, start: line.start, end: line.end });
    });
    return out;
  }, [transcript.lines]);
  const [fileCues, setFileCues] = useState<{ assetId: string; cues: ReturnType<typeof parseCaptionFile> } | null>(null);
  useEffect(() => {
    const assetId = list?.captions?.source === 'asset' ? list.captions.assetId : null;
    const asset = assetId ? kit?.assets.find((candidate) => candidate.id === assetId) : null;
    if (!asset || fileCues?.assetId === asset.id) return undefined;
    let cancelled = false;
    fetch(asset.url).then((response) => response.text()).then((text) => {
      if (!cancelled) setFileCues({ assetId: asset.id, cues: parseCaptionFile(text) });
    }).catch((error) => debugLog('Caption file could not be read', error));
    return () => { cancelled = true; };
  }, [fileCues, kit, list]);
  const cues = useMemo<PreviewCue[]>(() => {
    if (!list || !list.captions) return [];
    if (list.captions.source === 'asset') return fileCues && fileCues.assetId === list.captions.assetId ? placeFileCues(fileCues.cues, list) : [];
    return cuesFromWords(words, list, list.captions.style);
  }, [fileCues, list, words]);

  const canUndo = Boolean(history && history.past.length > 0);
  const canRedo = Boolean(history && history.future.length > 0);

  if (loadError) {
    return (
      <PageContainer sx={STUDIO_FRAME_SX}>
        <Alert severity="error" action={<Button onClick={() => navigate('/library')}>Library</Button>}>{loadError}</Alert>
      </PageContainer>
    );
  }
  if (!edit || !list || !source) {
    return (
      <PageContainer sx={STUDIO_FRAME_SX}>
        <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /><Typography variant="body2" sx={{ mt: 2 }}>Opening your edit…</Typography></Stack>
      </PageContainer>
    );
  }

  const saveLabel = saveState === 'saved' ? 'Saved' : saveState === 'pending' ? 'Unsaved changes' : saveState === 'saving' ? 'Saving…' : saveState === 'conflict' ? 'Changed elsewhere' : 'Not saved';

  return (
    <PageContainer sx={STUDIO_FRAME_SX}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/library')} aria-label="Back to the library"><ArrowBackIcon /></IconButton>
        <TextField
          variant="standard"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => { if (title.trim() && title.trim() !== edit.title) save(title.trim()); else setTitle(edit.title); }}
          inputProps={{ 'aria-label': 'Edit title', maxLength: 120, 'data-testid': 'edit-title', style: { fontSize: 20, fontWeight: 600 } }}
          sx={{ minWidth: 240, flex: 1 }}
        />
        <Chip size="small" label={saveLabel} color={saveState === 'conflict' || saveState === 'error' ? 'error' : saveState === 'saved' ? 'success' : 'default'} variant="outlined" data-testid="save-state" />
        <Tooltip title="Undo (⌘Z)"><span><IconButton onClick={() => setHistory((h) => (h ? undo(h) : h))} disabled={!canUndo} aria-label="Undo"><UndoIcon /></IconButton></span></Tooltip>
        <Tooltip title="Redo (⇧⌘Z)"><span><IconButton onClick={() => setHistory((h) => (h ? redo(h) : h))} disabled={!canRedo} aria-label="Redo"><RedoIcon /></IconButton></span></Tooltip>
        <Button variant="contained" onClick={() => setPanel('export')} data-testid="go-export">Export</Button>
      </Stack>
      {saveState === 'conflict' || saveState === 'error' ? (
        <Alert severity={saveState === 'conflict' ? 'warning' : 'error'} sx={{ mb: 2 }} action={saveState === 'conflict' ? <Button color="inherit" size="small" onClick={() => window.location.reload()}>Reload</Button> : <Button color="inherit" size="small" onClick={() => { setSaveState('pending'); save(); }}>Try again</Button>}>
          {saveMessage}
        </Alert>
      ) : null}
      {source.multicam ? <Alert severity="info" sx={{ mb: 2 }}>The preview shows the master angle. The export follows the camera cuts you made in the multicam studio.</Alert> : null}
      <CommandBar editId={edit.id} list={list} onApply={(next) => change(next)} onUndo={() => setHistory((h) => (h ? undo(h) : h))} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 380px' }, gap: 2.5, alignItems: 'start' }}>
        <Box>
          <Box sx={{ position: 'relative', bgcolor: '#000', borderRadius: '12px 12px 0 0', overflow: 'hidden', aspectRatio: '16 / 9', maxHeight: '62vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Captions are drawn by the preview layer, from the same cues the export uses. */}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video ref={player.ref} src={source.videoUrl} playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} data-testid="preview-video" />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', aspectRatio: player.videoWidth && player.videoHeight ? `${player.videoWidth} / ${player.videoHeight}` : '16 / 9', height: player.videoWidth && player.videoHeight && player.videoWidth / player.videoHeight < 16 / 9 ? '100%' : 'auto', width: player.videoWidth && player.videoHeight && player.videoWidth / player.videoHeight < 16 / 9 ? 'auto' : '100%', maxHeight: '100%', maxWidth: '100%' }}>
                <PreviewOverlay list={list} outputMs={player.outputMs} assets={kit?.assets || []} cues={cues} videoWidth={player.videoWidth} videoHeight={player.videoHeight} />
              </Box>
            </Box>
          </Box>
          <PreviewTransport player={player} list={list} muted={muted} onToggleMute={() => setMuted((m) => !m)} selection={selection} selectedSegmentId={selectedSegmentId} onSelectSegment={setSelectedSegmentId} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {source.name} · result {Math.round(outputDurationMs(list) / 100) / 10} s · {edit.summary}
          </Typography>
        </Box>
        <Box sx={{ border: `1px solid ${brand.border}`, borderRadius: 3, bgcolor: 'background.paper', minHeight: 420 }}>
          <Tabs value={panel} onChange={(_, value) => setPanel(value as PanelKey)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: `1px solid ${brand.border}`, px: 1 }}>
            <Tab label="Cut" value="cut" />
            <Tab label="Looks" value="looks" />
            <Tab label="Captions" value="captions" />
            <Tab label="Sound" value="sound" />
            <Tab label="Frame" value="output" />
            <Tab label="Export" value="export" />
          </Tabs>
          <Box sx={{ p: 2 }}>
            {panel === 'cut' ? <CutPanel list={list} outputMs={player.outputMs} selection={selection} onSelection={setSelection} selectedSegmentId={selectedSegmentId} onSelectSegment={setSelectedSegmentId} onChange={change} onSeek={player.seek} /> : null}
            {panel === 'looks' ? <LooksPanel list={list} kit={kit} outputMs={player.outputMs} onChange={change} onOpenBrandKit={() => setKitOpen(true)} /> : null}
            {panel === 'captions' ? <CaptionsPanel list={list} kit={kit} onChange={change} transcript={{ phase: transcript.phase, wordCount: words.length, canStart: Boolean(source.canTranscribe), start: () => { transcript.start(); }, problem: transcript.problem }} /> : null}
            {panel === 'sound' ? <SoundPanel list={list} kit={kit} outputMs={player.outputMs} onChange={change} onOpenBrandKit={() => setKitOpen(true)} voices={voices} onSpeak={speak} /> : null}
            {panel === 'output' ? <OutputPanel list={list} allowance={allowance} onChange={change} /> : null}
            {panel === 'export' ? <ExportsPanel editId={edit.id} editVersion={edit.version} dirty={dirty || saveState === 'pending' || saveState === 'saving'} outputDurationMs={outputDurationMs(list)} resolution={list.output.resolution} allowance={allowance} onAllowanceChanged={refreshAllowance} /> : null}
          </Box>
        </Box>
      </Box>
      <BrandKitDialog open={kitOpen} kit={kit} onClose={() => setKitOpen(false)} onKit={(updated) => { setKit(updated); setToast('Brand kit updated'); }} />
      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast(null)} message={toast} />
    </PageContainer>
  );
};

const EMPTY_LIST: EditList = {
  version: 1,
  source: { kind: 'video', videoId: '' },
  sourceDurationMs: 1,
  output: { resolution: '1080p', aspect: 'source', fps: null },
  segments: [],
  multicamCut: [],
  overlays: [],
  texts: [],
  captions: null,
  music: null,
  voiceovers: [],
  audio: { originalGainDb: 0, normalize: true },
  reframe: null,
};

const h2IsSame = (a: EditList, b: EditList): boolean => JSON.stringify(a) === JSON.stringify(b);

export default EditStudio;
